import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MediaDialogData } from '../../interfaces/media-dialog-data';
import { MediaType } from '../../types/media-type';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MediaTypes } from '../../enums/media-types';
import { FORM_FIELDS } from './constants/form-fields';
import { FORM_PATHS } from './constants/form-paths';
import { FormArray, FormBuilder } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { ImagesService } from 'src/app/shared/services/images.service';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  switchMap,
  tap,
} from 'rxjs/operators';
import { ITag, ITagCMSApi } from 'src/app/core/models/i-tag';
import { IPager } from 'src/app/shared/standalone/components/pager/interfaces/i-pager';
import { MatLegacyAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import {
  MAT_CHECKBOX_DEFAULT_OPTIONS,
  MatCheckboxDefaultOptions,
} from '@angular/material/checkbox';
import { IMedia } from 'src/app/core/models/i-media';
import { MediaService } from 'src/app/shared/services/media.service';
import { AppConfig } from 'src/app/app-config';

class MediaAdderDialogComponentBase {
  constructor() {}
}

const MediaAdderDialogComponentMixinBase = WithProcessing(
  MediaAdderDialogComponentBase
);

@Component({
  selector: 'app-media-adder-dialog',
  templateUrl: './media-adder-dialog.component.html',
  styleUrls: ['./media-adder-dialog.component.scss'],
  providers: [
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions,
    },
  ],
})
export class MediaAdderDialogComponent
  extends MediaAdderDialogComponentMixinBase
  implements OnInit
{
  mediaType!: MediaType;
  MEDIA_TYPES = MediaTypes;
  selectedMedias: IMedia[] = [];
  partiallySelectedMedias: IMedia[] = [];
  FORM_FIELDS = FORM_FIELDS;
  FORM_PATHS = FORM_PATHS;
  currentReponseItems!: IMedia[];
  currentTotalCount!: number;
  prevSearchedValue!: string;
  canSelectMultiple: boolean;
  tags$!: Observable<ITagCMSApi[]>;

  pager: IPager = {
    currentPage: 1,
    currentPageSize: 12,
    pageSizes: [12, 24, 48],
  };

  filteredMedias$: Observable<{
    results: Partial<IMedia>[];
    totalCount: number;
  }>;

  form = this.formBuilder.group({
    [this.FORM_FIELDS.SEARCH_TERM]: [''],
    [this.FORM_FIELDS.SEARCH_TAGS_TERM]: [''],
    [this.FORM_FIELDS.SEARCH_TAGS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.SELECT_ALL]: [false],
    [this.FORM_FIELDS.SHOW_ARTICLE_READY_ONLY]: [true],
    [this.FORM_FIELDS.THREE_BY_ONE_FILTER]: [false],
  });

  private MEDIA_TYPE_REQUEST_BUILDERS = {
    [MediaTypes.IMAGE]: () => ({
      SearchTerm: this.form.get(this.FORM_PATHS.SEARCH_TERM()).value?.trim(),
      Approved: true,
      Deleted: false,
      ArticleReady: !this.canSelectMultiple
        ? this.form.get(this.FORM_PATHS.SHOW_ARTICLE_READY_ONLY()).value
          ? this.form.get(this.FORM_PATHS.SHOW_ARTICLE_READY_ONLY()).value
          : null
        : null,
      PageNumber: this.pager.currentPage,
      PageSize: this.pager.currentPageSize,
      Tags: this.form.get(this.FORM_PATHS.SEARCH_TAGS()).value ?? [],
      GalleryReady: null,
      HasThreeByOne: this.form.get(this.FORM_PATHS.THREE_BY_ONE_FILTER()).value
        ? this.form.get(this.FORM_PATHS.THREE_BY_ONE_FILTER()).value
        : null,
      LegacyContentTypeCode: '',
      LegacyContentTypeFilter: {},
    }),
    [MediaTypes.VIDEO]: () => ({
      searchText: this.form.get(this.FORM_PATHS.SEARCH_TERM()).value?.trim(),
    }),
  };

  @ViewChild(MatLegacyAutocompleteTrigger)
  tagsAutocomplete: MatLegacyAutocompleteTrigger;

  constructor(
    public dialogRef: MatDialogRef<MediaAdderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MediaDialogData,
    private formBuilder: FormBuilder,
    private mediaService: MediaService,
    private imagesService: ImagesService,
    private appConfig: AppConfig
  ) {
    super();
    this.canSelectMultiple = data.canSelectMultiple;
    this.mediaType = data.mediaType;
    this.selectedMedias =
      this.mediaType === MediaTypes.IMAGE
        ? data.prevSelectedMedias
        : data.prevSelectedMedias.map((x) =>
            this.mediaService.mapVideoToMedia(x)
          );
  }

  ngOnInit(): void {
    this.selectedMedia.forEach((x) => (x.canUseInArticle = true));
    this._requestMedia();
    this._initTags();
  }

  handleInput(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  get selectedMedia(): IMedia[] {
    return this.selectedMedias;
  }

  private _initTags(): void {
    this.tags$ = this.form
      .get(this.FORM_PATHS.SEARCH_TAGS_TERM())
      .valueChanges.pipe(
        filter((v) => typeof v === 'string'),
        distinctUntilChanged(),
        debounceTime(1000),
        tap(() => {
          this.form.get(this.FORM_PATHS.SEARCH_TAGS_TERM()).disable();
        }),
        switchMap((searchedValue) => {
          this.prevSearchedValue = searchedValue;

          const getDataObs$ = searchedValue
            ? this.imagesService.getFilteredTags$(
                { currentPage: 1, currentPageSize: 15 },
                { filters: { searchText: searchedValue.trim() } }
              )
            : of(null);

          return getDataObs$.pipe(
            finalize(() => {
              this.form.get(this.FORM_PATHS.SEARCH_TAGS_TERM()).enable();
            })
          );
        })
      );

    this.form
      .get(this.FORM_PATHS.THREE_BY_ONE_FILTER())
      .valueChanges.subscribe({ next: () => this._requestMedia() });

    this.form
      .get(this.FORM_PATHS.SHOW_ARTICLE_READY_ONLY())
      .valueChanges.subscribe({ next: () => this._requestMedia() });
  }

  onToggleMediaSelection(selectedMedia: IMedia, keepSelected = false): void {
    if (!selectedMedia.canUseInArticle) {
      return null;
    }

    const isMediaSelected = this.selectedMedias
      .map((psm) => psm.id)
      .includes(selectedMedia.id);

    if (isMediaSelected && !keepSelected) {
      this.selectedMedias = this.selectedMedias.filter(
        (psm) => psm.id !== selectedMedia.id
      );
    }

    if (!isMediaSelected) {
      this.selectedMedias = this.canSelectMultiple
        ? [...this.selectedMedias, selectedMedia]
        : [selectedMedia];
    }
  }

  onToggleSelectAll(): void {
    this.form
      .get(this.FORM_PATHS.SELECT_ALL())
      .setValue(!this.form.get(this.FORM_PATHS.SELECT_ALL()).value);
    this.currentReponseItems.forEach((media) => {
      if (media.canUseInArticle) {
        this.onToggleMediaSelection(
          media,
          this.form.get(this.FORM_PATHS.SELECT_ALL()).value
        );
      }
    });
  }

  onToggle3by1() {
    this.form
      .get(this.FORM_PATHS.THREE_BY_ONE_FILTER())
      .setValue(!this.form.get(this.FORM_PATHS.THREE_BY_ONE_FILTER()).value);
  }

  onToggleArticleReadyOnly() {
    this.form
      .get(this.FORM_PATHS.SHOW_ARTICLE_READY_ONLY())
      .setValue(
        !this.form.get(this.FORM_PATHS.SHOW_ARTICLE_READY_ONLY()).value
      );
  }

  onSelectMedias(): void {
    this.selectedMedias = [
      ...this.selectedMedias,
      ...this.partiallySelectedMedias,
    ];
  }

  //#region TODO IMPROVEMENT: Create withPager mixin
  onPageSizeChanged(newPageSize: number): void {
    this.pager = {
      ...this.pager,
      currentPage: 0,
      currentPageSize: newPageSize,
    };
    this.form.get(this.FORM_PATHS.SELECT_ALL()).setValue(false);
    this._requestMedia();
  }

  onPageChanged(newPage: number): void {
    this.pager = { ...this.pager, currentPage: newPage };
    this.form.get(this.FORM_PATHS.SELECT_ALL()).setValue(false);
    this._requestMedia();
  }

  onSearch(): void {
    this.pager = { ...this.pager, currentPage: 0 };
    this.form.get(this.FORM_PATHS.SELECT_ALL()).setValue(false);
    this._requestMedia();
  }
  //#endregion

  private _requestMedia(): void {
    this.filteredMedias$ = this.mediaService
      .getFiltered$(this.mediaType, this.pager, {
        filters: this.MEDIA_TYPE_REQUEST_BUILDERS[this.mediaType](),
      })
      .pipe(tap((res) => this._setCurrentData(res)));
  }

  private _setCurrentData(response): void {
    let { results, totalCount } = response;

    this.currentReponseItems = [...results];
    this.currentTotalCount = totalCount;
  }

  onTagSelected(_): void {
    this.form
      .get(this.FORM_PATHS.SEARCH_TAGS_TERM())
      .setValue(this.prevSearchedValue);

    this._openTagsAutocompletePanel();
  }

  onTagClick(selectedTag: ITagCMSApi): void {
    this.onAddTagClick(selectedTag, false);
    this.tagsAutocomplete.closePanel();
  }

  onAddTagClick(selectedTag: ITagCMSApi, openPanel = true): void {
    const searchedTagsControl = this.form.get(
      this.FORM_PATHS.SEARCH_TAGS()
    ) as FormArray;

    searchedTagsControl.push(this.formBuilder.control(selectedTag));

    if (openPanel) {
      this._openTagsAutocompletePanel();
    }

    this._requestMedia();
  }

  onRemoveTag(tagToRemove: ITagCMSApi): void {
    const searchedTagsFrmCtrl = this.form.get(
      this.FORM_PATHS.SEARCH_TAGS()
    ) as FormArray;

    // TODO IMPROVEMENT: Use id instead of text field, now id comming always null
    const tagToRemoveIndex = searchedTagsFrmCtrl.value
      .map((tag) => tag.text)
      .indexOf(tagToRemove.text);
    searchedTagsFrmCtrl.removeAt(tagToRemoveIndex);
    this._requestMedia();
  }

  private _openTagsAutocompletePanel(): void {
    requestAnimationFrame(() => {
      this.tagsAutocomplete.openPanel();
    });
  }

  isMediaSelected(media: IMedia): boolean {
    return this.selectedMedias.map((psm) => psm.id).includes(media.id);
  }

  isTagSelected(tagToCheck): boolean {
    // TODO IMPROVEMENT: Use id instead of text field, now id comming always null
    return this.form
      .get(this.FORM_PATHS.SEARCH_TAGS())
      .value.some((selectedTag) => selectedTag.text === tagToCheck.text);
  }

  /* istanbul ignore next */
  drop(event: CdkDragDrop<IMedia[]>): void {
    moveItemInArray(
      this.selectedMedias,
      event.previousIndex,
      event.currentIndex
    );
  }

  mapToMedia() {
    return this.mediaType === MediaTypes.IMAGE
      ? this.selectedMedias.map((x) => this.mediaService.mapToImage(x))
      : this.selectedMedia.map((x) => this.mediaService.mapToVideo(x));
  }
}
